import {
  addDays, addMonths, addYears, endOfMonth, endOfWeek, format, getDay, getDaysInMonth, isAfter, isBefore, isSameDay,
  isSameMonth, isValid, parseISO, startOfMonth, startOfWeek, subMonths, subYears
} from 'date-fns'
import React, {
  CSSProperties, ReactElement, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState
} from 'react'
import { createPortal } from 'react-dom'
import styled, { css, keyframes } from 'styled-components'
import { TextInput } from './Input'

// ---------------------------------------------------------------------------------------------------------------------
// One date field for the whole dashboard: type the day as DD/MM/YYYY, paste an ISO date, or pick it from the calendar
// that opens underneath. A chosen day is a local Date at noon, so `toISOString()` names the same calendar day in every
// time zone.
// ---------------------------------------------------------------------------------------------------------------------

const NOON = 12
const EARLIEST = new Date(1900, 0, 1, NOON)
const LATEST = new Date(2100, 11, 31, NOON)
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

type View = 'days' | 'months' | 'years'

const atNoon = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate(), NOON)

/**
 * The day a value stands for, or undefined. A date-only string ("1990-05-12") is that day where the viewer is:
 * `new Date("1990-05-12")` would be UTC midnight, the day before west of Greenwich.
 */
export const toDay = (value: Date | string | null | undefined): Date | undefined => {
  if (!value) return undefined
  const date = value instanceof Date
    ? value
    : /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseISO(value) : new Date(value)
  return isValid(date) ? atNoon(date) : undefined
}

const dayKey = (d?: Date): string => d ? format(d, 'yyyy-MM-dd') : ''
const longDay = (d: Date): string => format(d, 'd MMM yyyy')
const clampDay = (d: Date, start: Date, end: Date): Date => isBefore(d, start) ? start : isAfter(d, end) ? end : d

/** The digits of what was typed, as DDMMYYYY. A separator after a single digit pads it ("5/" reads as "05"). */
const readTyped = (raw: string): string => {
  const iso = /^\s*(\d{4})-(\d{2})-(\d{2})\s*$/.exec(raw)
  if (iso) return `${iso[3]}${iso[2]}${iso[1]}`
  const segments = ['', '', '']
  let at = 0
  for (const ch of raw) {
    if (/\d/.test(ch)) {
      const max = at === 2 ? 4 : 2
      if (segments[at].length < max) {
        segments[at] += ch
      } else if (at < 2) {
        at += 1
        segments[at] += ch
      }
    } else if (/[/.\- ]/.test(ch) && at < 2 && segments[at].length > 0) {
      if (segments[at].length === 1) segments[at] = `0${segments[at]}`
      at += 1
    }
  }
  return segments.join('')
}

/** "15/09/2026" for the digits typed so far, with a slash appearing once the next segment starts. */
const formatDigits = (digits: string): string => {
  let out = digits.slice(0, 2)
  if (digits.length > 2) out += `/${digits.slice(2, 4)}`
  if (digits.length > 4) out += `/${digits.slice(4, 8)}`
  return out
}

const caretAfterDigits = (formatted: string, count: number): number => {
  let seen = 0
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) {
      seen += 1
      if (seen === count) return i + 1
    }
  }
  return count === 0 ? 0 : formatted.length
}

// ---------------------------------------------------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------------------------------------------------

const popIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const focusRing = css`
  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(49, 87, 198, 0.28);
    outline: none;
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  width: 100%;
`

const Field = styled.div`
  position: relative;
  width: 100%;
`

const Input = styled(TextInput)<{ $invalid?: boolean }>`
  font-variant-numeric: tabular-nums;
  padding-right: 44px;

  ${({ $invalid }) => $invalid && css`
    border-color: var(--pill-red-ink, #c51735);

    &:hover,
    :focus {
      border-color: var(--pill-red-ink, #c51735);
    }
  `}
`

const CalendarButton = styled.button<{ $open: boolean }>`
  align-items: center;
  background: ${({ $open }) => $open ? 'var(--surface-pressed, #eef1f7)' : 'transparent'};
  border: 0;
  border-radius: 9px;
  color: ${({ $open }) => $open ? 'var(--ink, #20242d)' : 'var(--text-muted, #6b7280)'};
  cursor: pointer;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  padding: 0;
  position: absolute;
  right: 7px;
  top: 50%;
  transform: translateY(-50%);
  transition: background-color 0.14s ease, color 0.14s ease, transform 0.1s ease;
  width: 30px;

  &:hover:not(:disabled) {
    background: var(--surface-hover, #f5f7fb);
    color: var(--ink, #20242d);
  }

  &:active:not(:disabled) {
    transform: translateY(-50%) scale(0.94);
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }

  ${focusRing}
`

const FieldError = styled.span`
  color: var(--pill-red-ink, #c51735);
  font-size: 12px;
  line-height: 1.35;
`

const Popover = styled.div`
  animation: ${popIn} 0.14s ease-out;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e3e7ef);
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14), 0 2px 6px rgba(15, 23, 42, 0.06);
  box-sizing: border-box;
  color: var(--ink, #20242d);
  display: flex;
  flex-direction: column;
  font-family: var(--font-sans);
  gap: 8px;
  padding: 12px 14px 12px;
  position: fixed;
  user-select: none;
  width: min(284px, calc(100vw - 16px));
  z-index: 10000;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const Header = styled.div`
  align-items: center;
  display: flex;
  gap: 2px;
  justify-content: space-between;
`

const HeaderButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: var(--ink-strong, #121722);
  cursor: pointer;
  display: inline-flex;
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 600;
  gap: 6px;
  letter-spacing: -0.01em;
  line-height: 1.2;
  padding: 6px 8px;
  transition: background-color 0.14s ease, transform 0.1s ease;

  &:hover {
    background: var(--surface-hover, #f5f7fb);
  }

  &:active {
    transform: scale(0.97);
  }

  ${focusRing}
`

const NavButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 999px;
  color: var(--text-muted, #6b7280);
  cursor: pointer;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  padding: 0;
  transition: background-color 0.14s ease, color 0.14s ease, transform 0.1s ease;
  width: 30px;

  &:hover:not(:disabled) {
    background: var(--surface-hover, #f5f7fb);
    color: var(--ink, #20242d);
  }

  &:active:not(:disabled) {
    transform: scale(0.92);
  }

  &:disabled {
    cursor: default;
    opacity: 0.3;
  }

  ${focusRing}
`

const Weekdays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 36px);
  justify-content: space-between;
`

const Weekday = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 11px;
  font-weight: 500;
  line-height: 24px;
  text-align: center;
`

const Days = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 36px);
  justify-content: space-between;
  row-gap: 2px;
`

const DayButton = styled.button<{ $selected: boolean, $today: boolean }>`
  background: ${({ $selected }) => $selected ? 'var(--primary-btn-bg, #171717)' : 'transparent'};
  border: 0;
  border-radius: 999px;
  box-shadow: ${({ $today, $selected }) => $today && !$selected ? 'inset 0 0 0 1px var(--border-strong, #d6dae2)' : 'none'};
  color: ${({ $selected }) => $selected ? 'var(--primary-btn-ink, #ffffff)' : 'var(--ink, #20242d)'};
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: ${({ $selected }) => $selected ? 600 : 500};
  height: 36px;
  line-height: 1;
  padding: 0;
  transition: background-color 0.12s ease, transform 0.1s ease;
  width: 36px;

  &:hover:not(:disabled) {
    background: ${({ $selected }) => $selected ? 'var(--primary-btn-bg, #171717)' : 'var(--surface-hover, #f5f7fb)'};
  }

  &:active:not(:disabled) {
    transform: scale(0.92);
  }

  &:disabled {
    color: var(--text-disabled, #a4aab5);
    cursor: default;
  }

  ${focusRing}
`

const OptionGrid = styled.div<{ $columns: number, $scroll?: boolean }>`
  display: grid;
  gap: 4px;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  ${({ $scroll }) => $scroll && css`
    max-height: 236px;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-right: 2px;
  `}
`

const OptionButton = styled.button<{ $selected: boolean }>`
  background: ${({ $selected }) => $selected ? 'var(--primary-btn-bg, #171717)' : 'transparent'};
  border: 0;
  border-radius: 10px;
  color: ${({ $selected }) => $selected ? 'var(--primary-btn-ink, #ffffff)' : 'var(--ink, #20242d)'};
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: ${({ $selected }) => $selected ? 600 : 500};
  height: 38px;
  line-height: 1;
  padding: 0 6px;
  transition: background-color 0.12s ease, transform 0.1s ease;

  &:hover:not(:disabled) {
    background: ${({ $selected }) => $selected ? 'var(--primary-btn-bg, #171717)' : 'var(--surface-hover, #f5f7fb)'};
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }

  &:disabled {
    color: var(--text-disabled, #a4aab5);
    cursor: default;
  }

  ${focusRing}
`

const Footer = styled.div`
  align-items: center;
  border-top: 1px solid var(--hairline, #eef1f5);
  display: flex;
  justify-content: space-between;
  margin-top: 2px;
  padding-top: 8px;
`

const TextAction = styled.button`
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: var(--ink, #20242d);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.2;
  padding: 6px 8px;
  transition: background-color 0.14s ease, transform 0.1s ease;

  &:hover {
    background: var(--surface-hover, #f5f7fb);
  }

  &:active {
    transform: translateY(1px);
  }

  ${focusRing}
`

const CalendarGlyph = (): ReactElement => (
  <svg viewBox='0 0 24 24' width='17' height='17' fill='none' stroke='currentColor' strokeWidth='1.8'
    strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <rect x='4' y='5' width='16' height='15' rx='3' />
    <path d='M4 10h16M8 3v4M16 3v4' />
  </svg>
)

const ChevronGlyph = ({ direction }: { direction: 'left' | 'right' | 'down' }): ReactElement => {
  const d = direction === 'left' ? 'm10 4-4 4 4 4' : direction === 'right' ? 'm6 4 4 4-4 4' : 'm4 6 4 4 4-4'
  return <svg viewBox='0 0 16 16' width='12' height='12' fill='none' stroke='currentColor' strokeWidth='1.8'
    strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'><path d={d} /></svg>
}

// ---------------------------------------------------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------------------------------------------------

export interface DateSelectorProps {
  value?: Date | string | null
  onChange: (date: Date | undefined) => void
  /** Earliest day that can be chosen, inclusive. `min` is the same thing under its older name. */
  startDate?: Date
  min?: Date
  /** Latest day that can be chosen, inclusive. `max` is the same thing under its older name. */
  endDate?: Date
  max?: Date
  /** The month the calendar opens on while nothing is chosen. */
  initialDate?: Date
  placeholder?: string
  disabled?: boolean
  id?: string
  name?: string
  ariaLabel?: string
  style?: CSSProperties
  innerStyle?: CSSProperties
}

const DateSelector = (props: DateSelectorProps): ReactElement => {
  const { onChange, disabled, placeholder = 'DD/MM/YYYY' } = props
  const start = atNoon(props.startDate ?? props.min ?? EARLIEST)
  const end = atNoon(props.endDate ?? props.max ?? LATEST)
  const startKey = dayKey(start)
  const endKey = dayKey(end)
  const today = useMemo(() => atNoon(new Date()), [])
  const valueDay = toDay(props.value)
  const valueKey = dayKey(valueDay)

  const [text, setText] = useState(valueDay ? format(valueDay, 'dd/MM/yyyy') : '')
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<View>('days')
  const [probe, setProbe] = useState<Date>(() => clampDay(valueDay ?? props.initialDate ?? today, start, end))
  const [focusDay, setFocusDay] = useState<Date>(probe)
  const [pos, setPos] = useState<{ top: number, left: number } | null>(null)

  const committedRef = useRef<Date | undefined>(valueDay)
  const fieldRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const skipOpenRef = useRef(false)
  const gridFocusRef = useRef(false)
  const baseId = useId()
  const popoverId = `${baseId}-calendar`
  const errorId = `${baseId}-error`

  const emit = useCallback((day: Date | undefined): void => {
    if (dayKey(day) === dayKey(committedRef.current)) return
    committedRef.current = day
    onChange(day)
  }, [onChange])

  // Follow the value: the form behind the field can load or switch records after it mounted. A value the field
  // already shows changes nothing, so typing is never overwritten.
  useEffect(() => {
    if (valueKey === dayKey(committedRef.current)) return
    committedRef.current = valueDay
    setText(valueDay ? format(valueDay, 'dd/MM/yyyy') : '')
    setError(null)
    if (valueDay) {
      setProbe(valueDay)
      setFocusDay(valueDay)
    }
  }, [valueKey])

  const close = useCallback((): void => {
    setOpen(false)
    gridFocusRef.current = false
  }, [])

  const openCalendar = useCallback((): void => {
    const base = clampDay(committedRef.current ?? props.initialDate ?? today, start, end)
    setProbe(base)
    setFocusDay(base)
    setView('days')
    setOpen(true)
  }, [props.initialDate, startKey, endKey, today])

  const focusInput = (): void => {
    if (document.activeElement !== inputRef.current) {
      skipOpenRef.current = true
      inputRef.current?.focus()
    }
  }

  /** Reads the digits typed so far. `settled` is true on blur and Enter, when a half-typed date is a problem. */
  const applyDigits = (digits: string, settled: boolean): void => {
    if (digits.length === 0) {
      setError(null)
      emit(undefined)
      return
    }
    if (digits.length < 8) {
      setError(settled ? 'Enter the full date as DD/MM/YYYY' : null)
      emit(undefined)
      return
    }
    const day = Number(digits.slice(0, 2))
    const month = Number(digits.slice(2, 4))
    const year = Number(digits.slice(4, 8))
    const candidate = new Date(year, month - 1, day, NOON)
    const real = candidate.getFullYear() === year && candidate.getMonth() === month - 1 && candidate.getDate() === day
    if (!real) {
      setError('That date does not exist')
      emit(undefined)
      return
    }
    if (isBefore(candidate, start)) {
      setError(`Earliest date is ${longDay(start)}`)
      emit(undefined)
      return
    }
    if (isAfter(candidate, end)) {
      setError(`Latest date is ${longDay(end)}`)
      emit(undefined)
      return
    }
    setError(null)
    setProbe(candidate)
    setFocusDay(candidate)
    emit(candidate)
  }

  const select = (day: Date): void => {
    setText(format(day, 'dd/MM/yyyy'))
    setError(null)
    setProbe(day)
    setFocusDay(day)
    emit(day)
    close()
    focusInput()
  }

  const clear = (): void => {
    setText('')
    setError(null)
    emit(undefined)
    close()
    focusInput()
  }

  const onInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const input = e.target
    const raw = input.value
    const caret = input.selectionStart ?? raw.length
    const digits = readTyped(raw)
    const formatted = formatDigits(digits)
    setText(formatted)
    applyDigits(digits, false)
    if (caret < raw.length) {
      const digitsBefore = raw.slice(0, caret).replace(/\D/g, '').length
      const next = caretAfterDigits(formatted, digitsBefore)
      requestAnimationFrame(() => input.setSelectionRange(next, next))
    }
  }

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      if (open) {
        e.stopPropagation()
        close()
      }
    } else if (e.key === 'Enter') {
      e.preventDefault()
      applyDigits(readTyped(text), true)
      close()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) openCalendar()
      gridFocusRef.current = true
      setView('days')
      requestAnimationFrame(() => focusDayButton(focusDay))
    }
  }

  const onBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    const next = e.relatedTarget as Node | null
    if (next && (fieldRef.current?.contains(next) || popRef.current?.contains(next))) return
    applyDigits(readTyped(text), true)
    close()
  }

  const onFocus = (): void => {
    if (skipOpenRef.current) {
      skipOpenRef.current = false
      return
    }
    if (!disabled && !open) openCalendar()
  }

  // Clicks anywhere else close the calendar. Clicks inside it never reach the document (see the popover handlers).
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      const target = e.target as Node
      if (fieldRef.current?.contains(target) || popRef.current?.contains(target)) return
      close()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open, close])

  // Place the calendar under the field, or above it when the viewport ends first.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    const place = (): void => {
      const field = fieldRef.current
      const pop = popRef.current
      if (!field || !pop) return
      const r = field.getBoundingClientRect()
      const w = pop.offsetWidth
      const h = pop.offsetHeight
      const vw = window.innerWidth
      const vh = window.innerHeight
      let top = r.bottom + 6
      if (top + h > vh - 8) {
        const above = r.top - 6 - h
        top = above >= 8 ? above : Math.max(8, vh - 8 - h)
      }
      const left = Math.min(Math.max(8, r.left), Math.max(8, vw - 8 - w))
      setPos({ top, left })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, view, dayKey(probe)])

  const focusDayButton = (day: Date): void => {
    popRef.current?.querySelector<HTMLButtonElement>(`[data-day="${dayKey(day)}"]`)?.focus()
  }

  useEffect(() => {
    if (open && view === 'days' && gridFocusRef.current) focusDayButton(focusDay)
  }, [open, view, dayKey(focusDay)])

  // The year list opens scrolled to the year in view.
  useLayoutEffect(() => {
    if (!open || view !== 'years') return
    const grid = popRef.current?.querySelector<HTMLDivElement>('[data-years]')
    const el = grid?.querySelector<HTMLButtonElement>(`[data-year="${probe.getFullYear()}"]`)
    if (grid && el) grid.scrollTop = el.offsetTop - grid.clientHeight / 2 + el.offsetHeight / 2
  }, [open, view])

  const inRange = (day: Date): boolean => !isBefore(day, start) && !isAfter(day, end)
  const monthInRange = (month: Date): boolean =>
    !isBefore(endOfMonth(month), start) && !isAfter(startOfMonth(month), end)

  const moveFocus = (day: Date): void => {
    const next = clampDay(day, start, end)
    gridFocusRef.current = true
    setFocusDay(next)
    if (!isSameMonth(next, probe)) setProbe(next)
  }

  const onGridKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }
    if (e.key in moves) {
      e.preventDefault()
      moveFocus(addDays(focusDay, moves[e.key]))
    } else if (e.key === 'PageUp') {
      e.preventDefault()
      moveFocus(subMonths(focusDay, 1))
    } else if (e.key === 'PageDown') {
      e.preventDefault()
      moveFocus(addMonths(focusDay, 1))
    } else if (e.key === 'Home') {
      e.preventDefault()
      moveFocus(startOfWeek(focusDay, { weekStartsOn: 1 }))
    } else if (e.key === 'End') {
      e.preventDefault()
      moveFocus(endOfWeek(focusDay, { weekStartsOn: 1 }))
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (inRange(focusDay)) select(atNoon(focusDay))
    } else if (e.key === 'Escape') {
      e.stopPropagation()
      close()
      focusInput()
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Calendar views
  // -------------------------------------------------------------------------------------------------------------------

  const selected = committedRef.current
  const monthStart = startOfMonth(probe)
  const leadingBlanks = (getDay(monthStart) + 6) % 7
  const daysInMonth = getDaysInMonth(probe)
  const prevMonth = subMonths(monthStart, 1)
  const nextMonth = addMonths(monthStart, 1)

  const renderDays = (): ReactElement => <>
    <Header>
      <HeaderButton type='button' onClick={() => setView('months')} aria-label='Choose a month'>
        {format(probe, 'MMMM yyyy')}<ChevronGlyph direction='down' />
      </HeaderButton>
      <div style={{ display: 'flex', gap: 2 }}>
        <NavButton type='button' aria-label='Previous month' disabled={!monthInRange(prevMonth)}
          onClick={() => setProbe(prevMonth)}><ChevronGlyph direction='left' /></NavButton>
        <NavButton type='button' aria-label='Next month' disabled={!monthInRange(nextMonth)}
          onClick={() => setProbe(nextMonth)}><ChevronGlyph direction='right' /></NavButton>
      </div>
    </Header>
    <Weekdays aria-hidden='true'>
      {WEEKDAYS.map(d => <Weekday key={d}>{d}</Weekday>)}
    </Weekdays>
    <Days role='grid' aria-label={format(probe, 'MMMM yyyy')} onKeyDown={onGridKeyDown}>
      {Array.from({ length: leadingBlanks }, (_, i) => <span key={`blank-${i}`} aria-hidden='true' />)}
      {Array.from({ length: daysInMonth }, (_, i) => {
        const day = new Date(probe.getFullYear(), probe.getMonth(), i + 1, NOON)
        const isSelected = !!selected && isSameDay(day, selected)
        return <DayButton
          key={i}
          type='button'
          role='gridcell'
          data-day={dayKey(day)}
          tabIndex={isSameDay(day, focusDay) ? 0 : -1}
          aria-selected={isSelected}
          aria-label={longDay(day)}
          disabled={!inRange(day)}
          $selected={isSelected}
          $today={isSameDay(day, today)}
          onClick={() => select(day)}>
          {i + 1}
        </DayButton>
      })}
    </Days>
  </>

  const renderMonths = (): ReactElement => {
    const prevYear = subYears(probe, 1)
    const nextYear = addYears(probe, 1)
    return <>
      <Header>
        <HeaderButton type='button' onClick={() => setView('years')} aria-label='Choose a year'>
          {format(probe, 'yyyy')}<ChevronGlyph direction='down' />
        </HeaderButton>
        <div style={{ display: 'flex', gap: 2 }}>
          <NavButton type='button' aria-label='Previous year' disabled={prevYear.getFullYear() < start.getFullYear()}
            onClick={() => setProbe(prevYear)}><ChevronGlyph direction='left' /></NavButton>
          <NavButton type='button' aria-label='Next year' disabled={nextYear.getFullYear() > end.getFullYear()}
            onClick={() => setProbe(nextYear)}><ChevronGlyph direction='right' /></NavButton>
        </div>
      </Header>
      <OptionGrid $columns={3}>
        {MONTHS.map((name, i) => {
          const month = new Date(probe.getFullYear(), i, 1, NOON)
          return <OptionButton
            key={name}
            type='button'
            disabled={!monthInRange(month)}
            $selected={!!selected && isSameMonth(month, selected)}
            onClick={() => {
              const day = Math.min(probe.getDate(), getDaysInMonth(month))
              setProbe(clampDay(new Date(probe.getFullYear(), i, day, NOON), start, end))
              setView('days')
            }}>
            {name.slice(0, 3)}
          </OptionButton>
        })}
      </OptionGrid>
    </>
  }

  const renderYears = (): ReactElement => {
    const first = Math.max(EARLIEST.getFullYear(), start.getFullYear())
    const last = Math.min(LATEST.getFullYear(), end.getFullYear())
    return <>
      <Header>
        <HeaderButton type='button' onClick={() => setView('days')} aria-label='Back to the days'>
          {format(probe, 'MMMM yyyy')}<ChevronGlyph direction='down' />
        </HeaderButton>
      </Header>
      <OptionGrid $columns={4} $scroll data-years>
        {Array.from({ length: last - first + 1 }, (_, i) => first + i).map(year =>
          <OptionButton
            key={year}
            type='button'
            data-year={year}
            $selected={!!selected && selected.getFullYear() === year}
            onClick={() => {
              setProbe(clampDay(new Date(year, probe.getMonth(), 1, NOON), start, end))
              setView('months')
            }}>
            {year}
          </OptionButton>)}
      </OptionGrid>
    </>
  }

  const showToday = inRange(today) && !(selected && isSameDay(selected, today))
  const showClear = !!selected || text.length > 0

  const calendar = open && <Popover
    ref={popRef}
    id={popoverId}
    role='dialog'
    aria-label='Choose a date'
    style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? 'visible' : 'hidden' }}
    // Keep focus in the field while clicking around the calendar, and keep these clicks away from whatever the
    // field sits in (a modal would read them as clicks outside itself).
    onMouseDown={e => { e.preventDefault(); e.stopPropagation() }}
    onMouseUp={e => e.stopPropagation()}
    onClick={e => e.stopPropagation()}>
    {view === 'days' && renderDays()}
    {view === 'months' && renderMonths()}
    {view === 'years' && renderYears()}
    {(showToday || showClear) &&
      <Footer>
        <span>{showClear && <TextAction type='button' onClick={clear}>Clear</TextAction>}</span>
        <span>{showToday && <TextAction type='button' onClick={() => select(today)}>Today</TextAction>}</span>
      </Footer>}
  </Popover>

  return <Wrapper style={props.style}>
    <Field ref={fieldRef}>
      <Input
        ref={inputRef}
        id={props.id}
        name={props.name}
        type='text'
        inputMode='numeric'
        autoComplete='off'
        spellCheck={false}
        maxLength={10}
        placeholder={placeholder}
        value={text}
        disabled={disabled}
        aria-label={props.ariaLabel}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        aria-haspopup='dialog'
        $invalid={!!error}
        style={props.innerStyle}
        onChange={onInput}
        onKeyDown={onInputKeyDown}
        onFocus={onFocus}
        onBlur={onBlur} />
      <CalendarButton
        type='button'
        tabIndex={-1}
        aria-label={open ? 'Close the calendar' : 'Open the calendar'}
        disabled={disabled}
        $open={open}
        onMouseDown={e => e.preventDefault()}
        onClick={() => {
          if (open) {
            close()
          } else {
            openCalendar()
            focusInput()
          }
        }}>
        <CalendarGlyph />
      </CalendarButton>
    </Field>
    {error && <FieldError id={errorId} role='alert'>{error}</FieldError>}
    {typeof document !== 'undefined' && calendar && createPortal(calendar, document.body)}
  </Wrapper>
}

export default DateSelector
